"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.layerRules = layerRules;
const types_1 = require("./types");
/**
 * Picks the winning rules from layers of increasing authority.
 *
 * Pass layers lowest-authority-first — `[federal, state]`. For each
 * `limitKey`, the last layer that has any rule for that key wins, and the
 * layers below it are ignored for that key. Keys a higher layer says nothing
 * about keep whatever the lower layer had, so a state that only sets a
 * central time limit keeps the federal satellite limits.
 *
 * A higher layer replaces the whole group of rules under a key, not one rule
 * at a time. If a state sets a central time limit, it replaces *every*
 * federal central time limit — including ones that only apply in specific
 * circumstances, like the longer deadline for shipping waste over 200 miles.
 * A state that doesn't grant that longer deadline shouldn't have it applied
 * to its facilities. See `LimitKey`.
 *
 * A `NONE` rule — "this jurisdiction imposes no limit for this area type" —
 * resolves by the same principle, applied to a whole area instead of one key.
 * It counts only if its layer is the highest one with anything to say about
 * that area; then it wipes every lower layer's rules for that area and stands
 * alone. If a higher layer sets a real limit for the area, the `NONE` is
 * withdrawn and suppresses nothing, so the rules it would have displaced
 * survive to compete normally. Both directions fall out of one question —
 * which layer is the top one for this area — rather than a pre-pass and a
 * post-filter that can disagree.
 *
 * Whether that withdrawal rule is right for the planned `EPISODIC` layer is
 * an open question, and needs answering when that layer is wired up. It was
 * written for layers of increasing *jurisdiction*: a state that sets a real
 * limit for an area has genuinely re-legislated the area, so pulling a
 * federal `NONE` back out is the right call. An episodic layer is not a
 * higher jurisdiction — it is a temporal overlay inside one — yet as written
 * an episodic limit for an area would withdraw that state's `NONE` and
 * resurrect the very federal limit the state affirmatively abolished.
 * Restricting withdrawal to jurisdictional layers would need a way to tell
 * the two kinds of layer apart, which a list position alone does not give.
 *
 * Precedence is a layer's position in the list, not its `source` label; two
 * layers may share a source.
 *
 * Nothing here enforces one winner per key — the winning layer contributes
 * every applicable rule it has under that key, and the clock treats each as
 * its own limit. Whether that comes to exactly one depends on how the layer
 * is written: the federal central time limits are separated by mutually
 * exclusive conditions (over 200 miles or not), so only ever one of them is
 * applicable at a time. `ruleRegistry.test.ts` sweeps every context to check
 * the shipped content holds to that.
 */
function layerRules(layers) {
    // The highest layer with anything at all to say about each area type.
    const topLayerByArea = new Map();
    layers.forEach((layer, index) => {
        for (const rule of layer.rules) {
            const current = topLayerByArea.get(rule.areaType) ?? -1;
            topLayerByArea.set(rule.areaType, Math.max(current, index));
        }
    });
    // Areas whose top layer declares NONE. Only those areas get wiped.
    const closedAreas = new Set();
    layers.forEach((layer, index) => {
        for (const rule of layer.rules) {
            if (rule.limit.kind === "NONE" && topLayerByArea.get(rule.areaType) === index) {
                closedAreas.add(rule.areaType);
            }
        }
    });
    const effective = layers.map((layer, index) => ({
        source: layer.source,
        rules: layer.rules.filter((rule) => {
            if (closedAreas.has(rule.areaType)) {
                // The top layer said "no limit here": only its own rules survive.
                return topLayerByArea.get(rule.areaType) === index;
            }
            // No NONE stands for this area — a lower layer's NONE has been
            // withdrawn by a higher layer setting a real limit, so it drops out
            // and suppresses nothing.
            return rule.limit.kind !== "NONE";
        }),
    }));
    const winningLayerByKey = new Map();
    effective.forEach((layer, index) => {
        for (const rule of layer.rules) {
            winningLayerByKey.set((0, types_1.limitKey)(rule), index);
        }
    });
    // Walk the layers again rather than reading the map out, so the result
    // stays in a predictable order: lower layers first, then higher ones.
    const resolved = [];
    effective.forEach((layer, index) => {
        for (const rule of layer.rules) {
            if (winningLayerByKey.get((0, types_1.limitKey)(rule)) === index) {
                resolved.push({ rule, source: layer.source });
            }
        }
    });
    return resolved;
}
//# sourceMappingURL=layerRules.js.map